import React, { useState, useRef, useEffect } from 'react';
import styles from './MultiSelectDropDown.module.scss';
import classNames from 'classnames';
import { Icon } from 'Components';

interface Option {
	id: number;
	label: string;
}

interface MultiSelectDropdownProps {
	options: Option[];
	placeholder?: string;
	onChange?: (selectedIds: number[]) => void;
	defaultSelectedIds?: number[];
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
	options,
	placeholder = 'Select option',
	onChange,
	defaultSelectedIds = []
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState<number[]>(
		defaultSelectedIds
	);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	const handleSelectAll = (checked: boolean) => {
		const newSelectedIds = checked ? options.map((opt) => opt.id) : [];
		setSelectedIds(newSelectedIds);
		onChange?.(newSelectedIds);
	};

	const handleOptionToggle = (id: number, checked: boolean) => {
		const newSelectedIds = checked
			? [...selectedIds, id]
			: selectedIds.filter((selectedId) => selectedId !== id);

		setSelectedIds(newSelectedIds);
		onChange?.(newSelectedIds);
	};

	const isAllSelected =
		selectedIds.length === options.length && options.length > 0;
	const isSomeSelected =
		selectedIds.length > 0 && selectedIds.length < options.length;

	const getDisplayText = () => {
		if (selectedIds.length === 0) return placeholder;
		if (isAllSelected) return 'All';
		if (selectedIds.length === 1) {
			const selected = options.find((opt) => opt.id === selectedIds[0]);
			return selected?.label || placeholder;
		}
		return `(${selectedIds.length}) selected`;
	};

	return (
		<div className={styles.multiSelect} ref={dropdownRef}>
			<div
				className={classNames(styles.trigger, {
					[styles['trigger--open']]: isOpen
				})}
				onClick={toggleDropdown}
			>
				<span className={styles.triggerText}>{getDisplayText()}</span>
				<Icon
					type="chevronDown"
					style={{
						transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
					}}
				/>
			</div>

			{isOpen && (
				<div className={styles.dropdown}>
					<div className={styles.optionList}>
						{/* Select All Option */}
						<label className={styles.option}>
							<div className={styles.checkboxWrapper}>
								<input
									type="checkbox"
									checked={isAllSelected}
									ref={(input) => {
										if (input) {
											input.indeterminate = isSomeSelected;
										}
									}}
									onChange={(e) =>
										handleSelectAll(e.target.checked)
									}
									className={styles.checkbox}
								/>
								<span
									className={classNames(styles.checkmark, {
										[styles[
											'checkmark--indeterminate'
										]]: isSomeSelected
									})}
								>
									{isAllSelected && (
										<svg
											width="12"
											height="10"
											viewBox="0 0 12 10"
											fill="none"
										>
											<path
												d="M1 5L4.5 8.5L11 1"
												stroke="white"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									)}
									{isSomeSelected && !isAllSelected && (
										<svg
											width="12"
											height="2"
											viewBox="0 0 12 2"
											fill="none"
										>
											<path
												d="M1 1H11"
												stroke="white"
												strokeWidth="2"
												strokeLinecap="round"
											/>
										</svg>
									)}
								</span>
							</div>
							<span className={styles.optionLabel}>All</span>
						</label>

						{/* Individual Options */}
						{options.map((option) => (
							<label key={option.id} className={styles.option}>
								<div className={styles.checkboxWrapper}>
									<input
										type="checkbox"
										checked={selectedIds.includes(
											option.id
										)}
										onChange={(e) =>
											handleOptionToggle(
												option.id,
												e.target.checked
											)
										}
										className={styles.checkbox}
									/>
									<span className={styles.checkmark}>
										{selectedIds.includes(option.id) && (
											<svg
												width="12"
												height="10"
												viewBox="0 0 12 10"
												fill="none"
											>
												<path
													d="M1 5L4.5 8.5L11 1"
													stroke="white"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										)}
									</span>
								</div>
								<span className={styles.optionLabel}>
									{option.label}
								</span>
							</label>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default MultiSelectDropdown;
